import { getPricingConfig, getCachedPricingConfig, type PricingConfig } from '../config/pricingConfig';

export interface PricingInputs {
  msrp: number;
  age: number; // Vehicle age in years
  mileage: number; // Mileage in km
  downPayment: number;
  apr?: number; // Optional, defaults to config value
  markupCap?: number; // Optional, defaults to config value
  sourceScore?: number; // Optional, defaults to config value (base minimum credit score)
  safetyMargin?: number; // Optional, defaults to config value (bonus points per 12 months)
}

export interface PricingResult {
  monthly: number;
  term: number;
  salePrice: number;
  markup: number;
  minScore: number;
  reasoning: string;
  error?: string;
}

/**
 * Term selection matrix (months) based on vehicle age.
 *
 * Spec:
 * - 0–3 yrs -> 60 months
 * - 3–6 yrs -> 48 months
 * - 6–10 yrs -> 36 months
 * - 10 yrs -> 24 months (or ineligible)
 *
 * We interpret "10 yrs -> 24 months or ineligible" as:
 * - age === 10: allow (24 months)
 * - age > 10: ineligible
 */
const termForAge = (age: number): { term: number; ineligible: boolean } => {
  if (age > 10) return { term: 0, ineligible: true };
  if (age <= 3) return { term: 60, ineligible: false };
  if (age <= 6) return { term: 48, ineligible: false };
  if (age < 10) return { term: 36, ineligible: false };
  return { term: 24, ineligible: false }; // age === 10
};

/**
 * Term selection matrix (months) based on vehicle mileage (km).
 *
 * Spec:
 * - <=60k -> 60 months
 * - 60k–130k -> 48 months
 * - 130k–180k -> 36 months
 * - 180k -> 24 months or ineligible
 *
 * We interpret "180k -> 24 months or ineligible" as:
 * - mileage === 180000: allow (24 months)
 * - mileage > 180000: ineligible
 */
const termForMileage = (km: number): { term: number; ineligible: boolean } => {
  if (km > 180000) return { term: 0, ineligible: true };
  if (km <= 60000) return { term: 60, ineligible: false };
  if (km <= 130000) return { term: 48, ineligible: false };
  if (km < 180000) return { term: 36, ineligible: false };
  return { term: 24, ineligible: false }; // km === 180000
};

const roundMonthly = (val: number): number => {
  // Whole-dollar monthly payment (keeps markup math stable)
  return Math.max(0, Math.round(val));
};

/**
 * Calculate vehicle age from year
 */
export const calculateVehicleAge = (year: number): number => {
  const currentYear = new Date().getFullYear();
  return currentYear - year;
};

/**
 * Extract year from car name (e.g., "2020 Toyota Camry" -> 2020)
 */
export const extractYearFromCarName = (carName: string): number | null => {
  if (!carName) return null;
  const match = carName.match(/^(\d{4})/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Calculate pricing for a vehicle listing
 * Based on the halal auto pricing algorithm
 */
export const calculatePricing = async (inputs: PricingInputs): Promise<PricingResult> => {
  // Get current pricing config from API (with localStorage fallback)
  let config: PricingConfig;
  try {
    config = await getPricingConfig();
  } catch (error) {
    console.warn('Failed to fetch pricing config from API, using cached:', error);
    config = getCachedPricingConfig();
  }
  
  // Use provided values or fall back to config defaults
  const apr = inputs.apr ?? config.apr;
  const cap = inputs.markupCap ?? config.markupCap;
  const sourceScore = inputs.sourceScore ?? config.sourceScore;
  const safetyMargin = inputs.safetyMargin ?? config.safetyMargin;

  // Validate inputs
  if (!inputs.msrp || inputs.msrp <= 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'MSRP must be greater than 0'
    };
  }

  if (inputs.age < 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Vehicle age cannot be negative'
    };
  }

  if (inputs.mileage < 0) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Mileage cannot be negative'
    };
  }

  if (inputs.downPayment < 0 || inputs.downPayment >= inputs.msrp) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Down payment must be between 0 and MSRP'
    };
  }

  // Term selection matrix
  const ageTermInfo = termForAge(inputs.age);
  const mileageTermInfo = termForMileage(inputs.mileage);

  if (ageTermInfo.ineligible) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Ineligible: vehicle age exceeds the maximum allowed term tier.'
    };
  }

  if (mileageTermInfo.ineligible) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Ineligible: vehicle mileage exceeds the maximum allowed term tier.'
    };
  }

  // Final term = lower of the two tiers => lower months (more restrictive)
  let term = Math.min(ageTermInfo.term, mileageTermInfo.term);

  // Monthly & markup simulation
  const monthlyRate = apr / 100 / 12;
  const P_finance = inputs.msrp - inputs.downPayment;

  const simulate = (months: number) => {
    const monthlyUnrounded =
      (monthlyRate * P_finance) /
      (1 - Math.pow(1 + monthlyRate, -months));
    const monthly = roundMonthly(monthlyUnrounded);
    const salePrice = monthly * months + inputs.downPayment;
    const markup = (salePrice - inputs.msrp) / inputs.msrp;
    return { monthly, salePrice, markup };
  };

  let simulated = simulate(term);
  let markup = simulated.markup;

  // If markup exceeds cap, reduce term and retry.
  // Keep at least 24 months (shortest supported tier).
  while (markup > cap && term > 24) {
    term -= 12;
    simulated = simulate(term);
    markup = simulated.markup;
  }

  // If still exceeds cap at shortest term.
  if (markup > cap) {
    return {
      monthly: 0,
      term: 0,
      salePrice: 0,
      markup: 0,
      minScore: 0,
      reasoning: '',
      error: 'Markup exceeds cap even at the shortest allowed term. Please adjust MSRP or down payment.'
    };
  }

  const { monthly, salePrice } = simulated;

  // Minimum credit score logic:
  // base score + bonus per 12-month term segment
  // Example: base=660, bonusPer12=10, term=48 => 660 + 10*(48/12)=700
  const termSegments = Math.max(1, Math.round(term / 12));
  const minScore = sourceScore + safetyMargin * termSegments;

  // Generate reasoning
  const reasoning = `Age term=${ageTermInfo.term} months, mileage term=${mileageTermInfo.term} months. Final term uses the lower months (most restrictive): ${term} months. APR=${apr}% -> monthly rate=${(monthlyRate * 100).toFixed(4)}%. Monthly payment computed via amortization; markup=(sale−MSRP)/MSRP=${
    (markup * 100).toFixed(1)
  }% must be <= cap=${(cap * 100).toFixed(0)}%. Min credit score = base(${sourceScore}) + bonus(${safetyMargin} per 12 months) * ${termSegments} segments.`;

  return {
    term,
    monthly,
    salePrice: Math.round(salePrice * 100) / 100,
    markup: markup * 100,
    minScore,
    reasoning
  };
};

